import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 24px;
  color: #6b7280;
  a { color: #00a6c2; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 24px 0;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 16px;
`;

const FormSection = styled.div`
  max-width: 600px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  &:focus { outline: none; border-color: #00a6c2; }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #00a6c2; }
`;

const SubmitButton = styled.button`
  padding: 12px 24px;
  background: #00a6c2;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  &:hover { background: #0094ab; }
  &:disabled { background: #9ca3af; cursor: not-allowed; }
`;

interface TalentStatus {
  id: string;
  name: string;
}

function AdminTalentStatusPage() {
  const [talentStatus, setTalentStatus] = useState('');
  const [emails, setEmails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [statuses, setStatuses] = useState<TalentStatus[]>([]);

  useEffect(() => {
    // Fetch talent statuses
    const fetchStatuses = async () => {
      try {
        const res = await api.get('/admin2/users/talent_status/options.json');
        setStatuses(res.data.statuses || []);
      } catch {
        // Use default statuses
        setStatuses([
          { id: 'preapproved', name: 'preapproved' },
          { id: 'approved', name: 'approved' },
          { id: 'activated', name: 'activated' },
          { id: 'banned', name: 'banned' },
          { id: 'spam', name: 'spam' },
          { id: 'inactive', name: 'inactive' },
          { id: 'ghost', name: 'ghost' },
          { id: 'a', name: 'a' },
          { id: 'b', name: 'b' },
          { id: 'c', name: 'c' },
          { id: 'declined', name: 'declined' },
          { id: 'unresponsive', name: 'unresponsive' },
          { id: 'unassigned', name: 'unassigned' },
        ]);
      }
    };
    fetchStatuses();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!talentStatus) {
      toast.error('Please select a talent status');
      return;
    }
    
    if (!emails.trim()) {
      toast.error('Please enter at least one email address');
      return;
    }

    setSubmitting(true);
    try {
      // BE expects talent_status_id (not talent_status) and emails as comma-separated string
      await api.post('/admin2/users/talent_status.json', {
        talent_status_id: talentStatus,
        emails: emails
      });
      toast.success('Contributor talent status updated successfully');
      setEmails('');
      setTalentStatus('');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to update talent status');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminLayout title="Users" pageTitle="Update Talent Status">
      <Breadcrumb>
        <Link to="/admin2/users">Users</Link>
        <span>›</span>
        <span>Talent status</span>
        <span>›</span>
        <span>New</span>
      </Breadcrumb>

      <PageTitle>Input contributors to be updated</PageTitle>

      <FormSection>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Talent status</Label>
            <Select
              value={talentStatus}
              onChange={(e) => setTalentStatus(e.target.value)}
            >
              <option value="">Select a talent status</option>
              {statuses.map((status) => (
                <option key={status.id} value={status.id}>{status.name}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>User email addresses - comma seperated</Label>
            <Textarea
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="user1@example.com, user2@example.com"
            />
          </FormGroup>

          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? 'Processing...' : 'Mark contributor talent status'}
          </SubmitButton>
        </form>
      </FormSection>
    </AdminLayout>
  );
}

export default AdminTalentStatusPage;
